import React, { forwardRef, useState, useEffect, useMemo } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import { AI_MODELS, getModelById, getSettingOptions } from '../../../config/aiModels';

const ToggleButton = ({ value, selected, onClick, disabled, compact, children }) => (
  <button
    onClick={() => !disabled && onClick(value)}
    disabled={disabled}
    className={`
      ${compact ? 'px-2 py-0.5 text-[11px]' : 'px-3 py-1 text-sm'}
      rounded font-sourceSans font-semibold transition-colors
      ${disabled
        ? 'bg-ag-black-600/40 text-ag-black-400 cursor-not-allowed'
        : selected
          ? 'bg-ag-black-600 text-white'
          : 'bg-black text-ag-black-300 hover:bg-ag-black-600 hover:text-white'
      }
    `}
  >
    {children}
  </button>
);

/**
 * AiSettingsPanel
 *
 * Config-driven settings panel — all available options are derived from
 * the selected model's config in aiModels.js. When a model doesn't support
 * a setting (e.g. voiceover has no aspect ratio), that row is hidden.
 *
 * Props:
 *   compact  — reduces padding, font sizes and button sizes for narrow containers.
 *   inline   — renders without absolute positioning (embedded flow).
 */
const AiSettingsPanel = forwardRef(({
  isOpen,
  aspectRatio,
  onAspectRatioChange,
  outputCount,
  onOutputCountChange,
  length,
  onLengthChange,
  selectedModel,
  onModelChange,
  selectedVoiceId,
  onVoiceIdChange,
  inline = false,
  compact = false,
  filterModels = null,
}, ref) => {
  const [modelsExpanded, setModelsExpanded] = useState(false);
  const [voiceSearchQuery, setVoiceSearchQuery] = useState('');

  // Collapse sub-panels when the settings panel closes
  useEffect(() => {
    if (!isOpen) {
      setModelsExpanded(false);
      setVoiceSearchQuery('');
    }
  }, [isOpen]);

  // Current model config (drives which settings rows are visible)
  const modelConfig = useMemo(() => getModelById(selectedModel), [selectedModel]);

  // Derived capability flags from config
  const aspectRatioOptions = useMemo(() => getSettingOptions(selectedModel, 'aspectRatio'), [selectedModel]);
  const outputCountOptions = useMemo(() => getSettingOptions(selectedModel, 'outputCount'), [selectedModel]);
  const lengthOptions      = useMemo(() => getSettingOptions(selectedModel, 'length'), [selectedModel]);
  const voiceOptions       = useMemo(() => getSettingOptions(selectedModel, 'voices'), [selectedModel]);

  // Filter voices by search query
  const filteredVoices = useMemo(() => {
    if (!voiceOptions) return [];
    if (!voiceSearchQuery.trim()) return voiceOptions;
    const q = voiceSearchQuery.toLowerCase();
    return voiceOptions.filter((v) =>
      v.name.toLowerCase().includes(q) ||
      v.accent?.toLowerCase().includes(q) ||
      v.gender?.toLowerCase().includes(q) ||
      v.style?.toLowerCase().includes(q)
    );
  }, [voiceOptions, voiceSearchQuery]);

  // When model changes, auto-correct settings that are no longer valid
  useEffect(() => {
    if (!modelConfig) return;

    // If the current length isn't in the new model's options, pick the first
    if (lengthOptions && !lengthOptions.includes(length)) {
      onLengthChange(lengthOptions[0]);
    }

    // If the current aspect ratio isn't in the new model's options, pick the first
    if (aspectRatioOptions && !aspectRatioOptions.includes(aspectRatio)) {
      onAspectRatioChange(aspectRatioOptions[0]);
    }

    // If the current output count isn't in the new model's options, pick the first
    if (outputCountOptions && !outputCountOptions.includes(outputCount)) {
      onOutputCountChange(outputCountOptions[0]);
    }
  }, [selectedModel]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const activeModel = AI_MODELS.find((m) => m.id === selectedModel);

  /* ── Compact vs default spacing tokens ── */
  const rowPx   = compact ? 'px-2.5' : 'px-4';
  const rowPy   = compact ? 'py-1.5' : 'py-3';
  const rowCls  = `${rowPx} ${rowPy}`;
  const labelCls = compact
    ? 'text-[11px] font-sourceSans text-white'
    : 'text-sm font-sourceSans text-white';

  return (
    <div
      ref={ref}
      className={inline
        ? "w-full bg-ag-black-800 overflow-hidden"
        : "absolute bottom-full mb-[62px] left-1/2 -translate-x-1/2 z-50 w-[300px] bg-ag-black-800 rounded-xl shadow-2xl overflow-hidden"
      }
    >
      {/* Header */}
      <div className={`${rowPx} ${compact ? 'py-1.5' : 'py-3'} bg-black border border-ag-black-600 rounded-t-xl`}>
        <h2 className={`text-white ${compact ? 'text-xs' : 'text-base'} font-sourceSans font-semibold tracking-wide`}>
          controls
        </h2>
      </div>

      {/* Divider */}
      <div className="border-t border-ag-black-600" />

      {/* ── Models row ── */}
      <div className="border-b border-ag-black-600">
        <div className={`flex items-center justify-between ${rowCls}`}>
          <span className={labelCls}>Models</span>
          <div className="flex items-center gap-2">
            {activeModel && (
              <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-sourceSans text-ag-black-300`}>
                {activeModel.label}
              </span>
            )}
            {/* Three-dot toggle button */}
            <button
              onClick={() => setModelsExpanded((prev) => !prev)}
              className={`flex items-center gap-[3px] ${compact ? 'p-0.5' : 'p-1'} rounded hover:bg-ag-black-600 transition-colors`}
            >
              <span className={`${compact ? 'w-1 h-1' : 'w-[5px] h-[5px]'} rounded-full transition-colors ${modelsExpanded ? 'bg-ag-green' : 'bg-ag-black-400'}`} />
              <span className={`${compact ? 'w-1 h-1' : 'w-[5px] h-[5px]'} rounded-full transition-colors ${modelsExpanded ? 'bg-ag-green' : 'bg-ag-black-400'}`} />
              <span className={`${compact ? 'w-1 h-1' : 'w-[5px] h-[5px]'} rounded-full transition-colors ${modelsExpanded ? 'bg-ag-green' : 'bg-ag-black-400'}`} />
            </button>
          </div>
        </div>

        {/* Expanded model chips */}
        {modelsExpanded && (
          <div className={`${rowPx} ${compact ? 'pb-1.5' : 'pb-3'}`}>
            <div className={`flex flex-wrap ${compact ? 'gap-1' : 'gap-1.5'}`}>
              {(filterModels ? AI_MODELS.filter(m => filterModels.includes(m.id)) : AI_MODELS).map((model) => {
                const isSelected = selectedModel === model.id;
                return (
                  <button
                    key={model.id}
                    onClick={() => onModelChange(model.id)}
                    className={`
                      ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'}
                      rounded font-sourceSans font-semibold transition-colors
                      ${isSelected
                        ? 'bg-ag-black-600 text-white'
                        : 'bg-black text-ag-black-300 hover:bg-ag-black-600 hover:text-white'
                      }
                    `}
                  >
                    {model.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Aspect ratio row (only if model supports it) ── */}
      {aspectRatioOptions && (
        <div className={`flex items-center justify-between ${rowCls} border-b border-ag-black-600`}>
          <span className={labelCls}>Aspect ratio</span>
          <div className={`flex items-center ${compact ? 'gap-1' : 'gap-1.5'}`}>
            {aspectRatioOptions.map((ratio) => (
              <ToggleButton
                key={ratio}
                value={ratio}
                selected={aspectRatio === ratio}
                onClick={onAspectRatioChange}
                compact={compact}
              >
                {ratio}
              </ToggleButton>
            ))}
          </div>
        </div>
      )}

      {/* ── Number of output row (only if model supports it) ── */}
      {outputCountOptions && (
        <div className={`flex items-center justify-between ${rowCls} border-b border-ag-black-600`}>
          <span className={labelCls}>number of output</span>
          <div className={`flex items-center ${compact ? 'gap-1' : 'gap-1.5'}`}>
            {outputCountOptions.map((num) => (
              <ToggleButton
                key={num}
                value={num}
                selected={outputCount === num}
                onClick={onOutputCountChange}
                compact={compact}
              >
                {num}
              </ToggleButton>
            ))}
          </div>
        </div>
      )}

      {/* ── Length row (only if model supports it) ── */}
      {lengthOptions && (
        <div className={`flex items-center justify-between ${rowCls} border-b border-ag-black-600`}>
          <span className={labelCls}>Length</span>
          <div className={`flex items-center ${compact ? 'gap-1' : 'gap-1.5'}`}>
            {lengthOptions.map((len) => (
              <ToggleButton
                key={len}
                value={len}
                selected={length === len}
                onClick={onLengthChange}
                compact={compact}
              >
                {len}
              </ToggleButton>
            ))}
          </div>
        </div>
      )}

      {/* ── Voice selector (only for voiceover models) ── */}
      {voiceOptions && (
        <div className="border-b border-ag-black-600">
          <div className={`${rowCls}`}>
            <span className={labelCls}>Voice</span>

            {/* Search input */}
            <div className={`relative mt-1.5 ${compact ? 'mb-1' : 'mb-2'}`}>
              <Search size={compact ? 10 : 12} className="absolute left-2 top-1/2 -translate-y-1/2 text-ag-black-400" />
              <input
                type="text"
                value={voiceSearchQuery}
                onChange={(e) => setVoiceSearchQuery(e.target.value)}
                placeholder="Search voices..."
                className={`w-full ${compact ? 'pl-6 pr-2 py-0.5 text-[10px]' : 'pl-7 pr-2 py-1 text-xs'} bg-black rounded font-sourceSans text-white placeholder-ag-black-400 outline-none border border-ag-black-600 focus:border-ag-black-400 transition-colors`}
              />
            </div>

            {/* Voice list */}
            <div className={`max-h-[160px] overflow-y-auto ${compact ? 'space-y-0.5' : 'space-y-1'}`}
                  style={{ 
                  scrollbarWidth: 'thin', 
                  scrollbarColor: '#4A4A4A #1F1E1E' 
            }}>
              {filteredVoices.length === 0 && (
                <p className={`${compact ? 'text-[10px]' : 'text-xs'} font-sourceSans text-ag-black-400 italic py-1`}>
                  No voices found
                </p>
              )}
              {filteredVoices.map((voice) => {
                const isSelected = selectedVoiceId === voice.id;
                return (
                  <button
                    key={voice.id}
                    onClick={() => onVoiceIdChange(voice.id)}
                    className={`
                      w-full flex items-center justify-between
                      ${compact ? 'px-2 py-1 rounded' : 'px-2.5 py-1.5 rounded-lg'}
                      transition-colors text-left
                      ${isSelected
                        ? 'bg-ag-black-600 text-white'
                        : 'bg-transparent text-ag-black-300 hover:bg-ag-black-700 hover:text-white'
                      }
                    `}
                  >
                    <div>
                      <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-sourceSans font-semibold`}>
                        {voice.name}
                      </span>
                      <span className={`${compact ? 'text-[9px]' : 'text-[10px]'} font-sourceSans text-ag-black-400 ml-1.5`}>
                        {voice.accent} · {voice.gender}
                      </span>
                    </div>
                    {voice.style && (
                      <span className={`${compact ? 'text-[8px]' : 'text-[9px]'} font-sourceSans text-ag-black-400 ml-2 shrink-0`}>
                        {voice.style}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Presets row ── */}
      {/* <div className={`flex items-center justify-between ${rowCls}`}>
        <span className={labelCls}>Presets</span>
        <ChevronRight size={compact ? 12 : 16} className="text-ag-black-300" />
      </div> */}
    </div>
  );
});

AiSettingsPanel.displayName = 'AiSettingsPanel';

export default AiSettingsPanel;
